function moneyExact(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const value = Number(n);
  const sign = value < 0 ? '-' : '';
  return `${sign}$${Math.abs(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function replyText(data = {}) {
  return String(data.reply || data.text || '').trim();
}

function missingCostLabels(briefing) {
  const missing = briefing?.completeness?.missingInputs || [];
  const labels = {
    fuelActual: 'fuel purchases',
    fuel: 'estimated fuel',
    unitFixed: 'unit fixed costs',
    factoring: 'factoring',
  };
  return missing.map((key) => labels[key] || key);
}

function lineFlags(line) {
  const flags = [];
  if (!line || line.value == null || line.provenance === 'missing') {
    flags.push('missing');
    return flags;
  }
  if (line.provenance === 'estimated') flags.push('estimated');
  if (line.partial) flags.push('incomplete');
  return flags;
}

function fuelEstimateFlags(fuel) {
  if (!fuel || fuel.estimatedFuelCost == null || fuel.provenance === 'missing') {
    return ['missing'];
  }
  return fuel.provenance === 'estimated' ? ['estimated'] : [];
}

function usesSpokenReply(data = {}) {
  if (data.kind === 'fleet_briefing' && data.fleet?.trucks?.length) return false;
  return true;
}

function spokenWindow(window = {}) {
  if (!window.startDate || !window.endDate) return '';
  const dates = `${window.startDate} → ${window.endDate}`;
  if (window.period === 'this_week') return `this week (${dates})`;
  if (window.period === 'last_week') return `last week (${dates})`;
  return dates;
}

function visibleAnswer(data = {}) {
  const spoken = replyText(data);
  if (usesSpokenReply(data) && spoken) return spoken;
  if (data.kind === 'fleet_briefing' && data.fleet) {
    return `Company trucks ${spokenWindow(data.fleet.window)}`.trim();
  }
  return spoken;
}

function documentSlotLabel(value) {
  if (value === true) return 'yes';
  if (value === false) return 'no';
  return 'unknown';
}

function documentSlotValue(item, key) {
  if (!item) return null;
  const named = key === 'rc' ? 'rateConfirmation' : 'proofDelivery';
  const short = key === 'rc' ? 'rc' : 'pod';
  if (typeof item[short] === 'boolean') return item[short];
  const slot = [item[named], item.docs?.[named], item.documents?.[named]]
    .find((value) => value != null && value !== '');
  if (typeof slot === 'boolean') return slot;
  if (typeof slot?.attached === 'boolean') return slot.attached;
  return null;
}

function evidencedDocumentSlots(row = {}) {
  const sources = [row.documents, row.load && row.load.documents];
  for (const source of sources) {
    if (!source) continue;
    const rc = documentSlotValue(source, 'rc');
    const pod = documentSlotValue(source, 'pod');
    if (typeof rc === 'boolean' || typeof pod === 'boolean') {
      return {
        rc: typeof rc === 'boolean' ? rc : null,
        pod: typeof pod === 'boolean' ? pod : null,
      };
    }
  }
  return null;
}

function shouldShowDocumentTable(data = {}) {
  if (!data.preview?.length) return false;
  return data.kind === 'preview_delivery' || data.kind === 'documents';
}

function immediatePanel(data = {}) {
  if (data.kind === 'show_costs' && data.briefing) return 'costs';
  if (data.kind === 'compare_previous' && (data.briefing || data.previousBriefing)) return 'compare';
  if (data.kind === 'show_loads' && data.briefing) return 'loads';
  if (data.kind === 'show_miles' && data.briefing) return 'miles';
  if (data.kind === 'show_fuel' && data.briefing) return 'fuel';
  if (data.kind === 'attention' && data.attention) return 'attention';
  if (data.kind === 'mpg_scenario' && (data.mpgScenario || data.briefing)) return 'mpg';
  return null;
}

function coverageLabel(status) {
  if (status === 'no_recorded_driving') return 'No recorded driving as of this fetch';
  if (status === 'open') return 'Open day — not a final result';
  if (status === 'partial' || status === 'missing_evidence') return 'Still catching up';
  if (status === 'unresolved') return 'Unresolved — earlier evidence kept';
  if (status === 'recorded') return 'Recorded driving';
  return '';
}

function freshnessLabel(fetchedAt, now = Date.now()) {
  if (!fetchedAt) return 'Fetch time unknown';
  const date = new Date(fetchedAt);
  if (Number.isNaN(date.getTime())) return 'Fetch time unknown';
  const minutes = Math.floor((Number(now) - date.getTime()) / 60000);
  if (minutes < 1) return 'updated just now';
  if (minutes < 60) return `updated ${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `updated ${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `updated ${days} day${days === 1 ? '' : 's'} ago`;
  return `updated ${date.toISOString().slice(0, 10)}`;
}

function milesCoverageRows(briefing = {}) {
  return Array.isArray(briefing.milesCoverage?.daily) ? briefing.milesCoverage.daily : [];
}

function unitMilesCoverage(unit = {}) {
  return unit.milesCoverage || unit.miles?.coverage || null;
}

function fleetMilesCoverageNotice(perUnit = []) {
  const incomplete = (perUnit || []).filter((unit) => {
    const coverage = unitMilesCoverage(unit);
    if (!coverage) return false;
    return coverage.needsSpokenCatchup
      || coverage.status === 'partial'
      || coverage.status === 'missing_evidence'
      || coverage.status === 'unresolved'
      || coverage.status === 'open';
  });
  if (!incomplete.length) return null;
  const units = incomplete.map((unit) => unit.unitNumber).filter(Boolean).join(', ');
  return `Mileage is still catching up${units ? ` for ${units}` : ''}. Fuel on those days is not a final result.`;
}

function shouldAcceptChatResponse({
  sentEpoch,
  activeEpoch,
  sentConversationId = null,
  activeConversationId = null,
  sentOrgId = null,
  activeOrgId = null,
  sentUserId = null,
  activeUserId = null,
  sentRequestId = null,
  activeRequestId = null,
  matchRequestId = false,
} = {}) {
  if (Number(sentEpoch) !== Number(activeEpoch)) return false;
  if (!sentConversationId || sentConversationId !== activeConversationId) return false;
  if ((sentOrgId || null) !== (activeOrgId || null)) return false;
  if ((sentUserId || null) !== (activeUserId || null)) return false;
  if (matchRequestId && (!sentRequestId || sentRequestId !== activeRequestId)) return false;
  return true;
}

function historyIsStale(currentMessages = [], incoming = [], { loading = false } = {}) {
  if (loading) return true;
  const rows = incoming || [];
  if (rows.length < currentMessages.length) return true;
  const lastLocal = currentMessages[currentMessages.length - 1];
  if (lastLocal?.role === 'llama' && lastLocal.requestId) {
    const found = rows.some((row) => (
      row.role === 'llama' && row.requestId === lastLocal.requestId
    ));
    if (!found) return true;
  }
  return false;
}

function restoreComposer({ current, draft, accepted }) {
  if (!accepted) return current;
  return current || draft || '';
}

function newConversationId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `chat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function newRequestId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `turn-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function isLegacyLlamaEntry(pathname = '', search = '') {
  const path = String(pathname || '');
  if (!/\/fleet-intelligence\/?$/.test(path)) return false;
  const params = new URLSearchParams(search || '');
  return params.get('tab') === 'llama';
}

function canonicalLlamaPath(search = '', base = '/enhancedDashboard') {
  const params = new URLSearchParams(search || '');
  const next = new URLSearchParams();
  const conversationId = params.get('conversationId');
  if (conversationId) next.set('conversationId', conversationId);
  const query = next.toString();
  return `${base}/llama${query ? `?${query}` : ''}`;
}

function threadStorageKey({ env = 'app', orgId, userId } = {}) {
  if (!orgId || !userId) return null;
  return `llama.conversationId.${env}.${orgId}.${userId}`;
}

function normalizeAttachment(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const type = String(raw.type || '').trim().toLowerCase();
  const id = String(raw.id || '').trim();
  if (!id || !['load', 'invoice', 'truck'].includes(type)) return null;
  return { type, id };
}

function attachmentLabel(attachment) {
  const attached = normalizeAttachment(attachment);
  if (!attached) return '';
  if (attached.type === 'load') return `Load ${attached.id}`;
  if (attached.type === 'invoice') return `Invoice ${attached.id}`;
  return `Truck ${attached.id}`;
}

function conversationContextLine(focus = {}, briefing = {}) {
  const unit = briefing.unitNumber || focus.unitNumber || '';
  const start = briefing.window?.startDate || focus.startDate || '';
  const end = briefing.window?.endDate || focus.endDate || '';
  if (unit && start && end) return `Truck ${unit} · ${start} → ${end}`;
  if (unit) return `Truck ${unit}`;
  return '';
}

function mpgComparisonLine(scenario) {
  if (!scenario?.mpg) return '';
  return `${scenario.mpg} MPG comparison · Saved setting unchanged`;
}

const PNL_HEADER_KINDS = new Set([
  'unit_briefing',
  'show_costs',
  'show_loads',
  'show_miles',
  'show_fuel',
  'restore_original',
  'mpg_scenario',
  'fleet_briefing',
  'compare_previous',
]);

function lastAssistantRow(messages = []) {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const row = messages[i];
    if (row && row.role !== 'user' && row.kind) return row;
  }
  return null;
}

function openLoadNumber(row = {}) {
  const load = row.load;
  if (load && (load.lookupStatus === 'not_found' || load.found === false)) return '';
  return load?.loadNumber
    || row.estimate?.loadNumber
    || (row.attachment?.type === 'load' ? row.attachment.id : '')
    || '';
}

function windowLoadLabel(bundle) {
  if (!bundle || bundle.count == null) return '—';
  if (bundle.statusDeliveredCount == null) return `${bundle.count} in window`;
  return `${bundle.count} in window · ${bundle.statusDeliveredCount} marked delivered`;
}

function turnSubjectLine(row = {}) {
  if (!row) return '';
  if (row.kind === 'facility_history' || row.kind === 'lumper_record') {
    return row.facilityName || 'Facility';
  }
  if (row.load && row.load.found === false) {
    return row.load.loadNumber ? `Load ${row.load.loadNumber}` : '';
  }
  if (row.kind === 'invoices' && !row.load && !row.focus?.loadNumber) return 'Company invoices';
  const tripSubject = ['offer', 'route'].includes(row.focus?.conversation?.currentKind);
  if ((row.kind === 'saved_mpg' && !tripSubject) || (PNL_HEADER_KINDS.has(row.kind) && !row.briefing && row.kind !== 'saved_mpg')) {
    // Saved MPG is a fleet setting; only a truck's P&L window names a unit here.
    const start = row.focus?.startDate || row.focus?.pnl?.startDate || '';
    const end = row.focus?.endDate || row.focus?.pnl?.endDate || '';
    const unit = row.kind === 'saved_mpg'
      ? (row.focus?.pnl?.unitNumber || (start && end ? row.focus?.unitNumber : '') || '')
      : (row.focus?.unitNumber || row.focus?.pnl?.unitNumber || '');
    if (unit && start && end) return `Unit ${unit} · ${start} → ${end}`;
    if (unit) return `Unit ${unit}`;
    return '';
  }
  if (PNL_HEADER_KINDS.has(row.kind) && row.briefing) {
    return conversationContextLine({}, row.briefing);
  }
  if (row.assignments?.lookupStatus === 'ambiguous' || row.assignments?.resolution === 'ambiguous') {
    const unit = row.assignments?.unitNumber || row.focus?.unitNumber || '';
    return unit ? `Unit ${unit}` : '';
  }
  const load = row.load?.loadNumber
    || row.assignments?.current?.loadNumber
    || (row.focus?.loadNumber && row.assignments?.lookupStatus !== 'ambiguous' ? row.focus.loadNumber : '')
    || '';
  const remembered = row.focus?.load?.lastLoad;
  const loadUnit = load && remembered && String(remembered.loadNumber) === String(load)
    ? remembered.unitNumber
    : null;
  const unit = row.location?.unitNumber
    || row.assignments?.unitNumber
    || row.load?.unitNumber
    || loadUnit
    || (load ? '' : row.focus?.unitNumber)
    || '';
  if (unit && load) return `Unit ${unit} · load ${load}`;
  if (unit) return `Unit ${unit}`;
  if (load) return `Load ${load}`;
  return '';
}

function headerContextLine(messages = []) {
  return turnSubjectLine(lastAssistantRow(messages) || {});
}

function headerMpgLine(messages = []) {
  const last = lastAssistantRow(messages);
  if (last?.kind === 'mpg_scenario' && last.mpgScenario) {
    return mpgComparisonLine(last.mpgScenario);
  }
  return '';
}

function immediateQualifications(row = {}) {
  const notes = [];
  const coverage = row.briefing?.milesCoverage || {};
  const catching = (coverage.daily || [])
    .filter((day) => day.coverage === 'partial' || day.coverage === 'open' || day.coverage === 'missing_evidence')
    .map((day) => day.date)
    .filter(Boolean);
  if (coverage.needsSpokenCatchup || catching.length) {
    notes.push(catching.length
      ? `Miles still updating: ${catching.join(', ')}`
      : 'Miles still updating');
  }
  if (row.briefing?.fuel?.estimatedFuelCost != null) notes.push('Estimated fuel');
  missingCostLabels(row.briefing).forEach((label) => {
    notes.push(`Missing ${label}`);
  });
  return [...new Set(notes)];
}

function tableCells(line) {
  return String(line).trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((cell) => cell.trim());
}

// Splits a spoken reply into paragraphs, bullet lists, and pipe tables so the
// chat can render the evidence table as a table instead of raw text.
function replyBlocks(text) {
  const lines = String(text || '').split(/\n/).map((row) => row.trim()).filter(Boolean);
  const blocks = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    const next = lines[i + 1] || '';
    if (/^\|.*\|$/.test(line) && /^\|[\s:|-]+\|$/.test(next)) {
      const header = tableCells(line);
      const rows = [];
      i += 2;
      while (i < lines.length && /^\|.*\|$/.test(lines[i])) {
        rows.push(tableCells(lines[i]));
        i += 1;
      }
      i -= 1;
      blocks.push({ type: 'table', header, rows });
    } else if (/^[-•]\s+/.test(line)) {
      const items = [];
      while (i < lines.length && /^[-•]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-•]\s+/, '').replace(/\*\*/g, ''));
        i += 1;
      }
      i -= 1;
      blocks.push({ type: 'list', items });
    } else {
      blocks.push({ type: 'p', text: line.replace(/\*\*/g, '') });
    }
  }
  return blocks;
}

const SENTENCE_ABBREVIATIONS = new Set(['st', 'mt', 'ft', 'vs', 'approx', 'no', 'e.g', 'i.e']);

// A sentence ends at . ! or ? followed by whitespace or the end of the line,
// so "$1,119.86" and "1029.13" stay inside their sentence.
function splitSentences(line) {
  const text = String(line || '');
  const out = [];
  let start = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (!/[.!?]/.test(text[i])) continue;
    let end = i + 1;
    while (end < text.length && /[.!?)"'”’]/.test(text[end])) end += 1;
    if (end < text.length && !/\s/.test(text[end])) continue;
    const word = (text.slice(start, i).match(/([A-Za-z.]+)$/) || [])[1] || '';
    if (end < text.length && SENTENCE_ABBREVIATIONS.has(word.toLowerCase())) continue;
    const piece = text.slice(start, end).trim();
    if (piece) out.push(piece);
    start = end;
    i = end - 1;
  }
  const rest = text.slice(start).trim();
  if (rest) out.push(rest);
  return out;
}

const HEADLINE_FIGURE = /\$\s?\d|\b\d[\d,]*(?:\.\d+)?\s*(?:loads?|trucks?|truck-days?|days?|miles?|invoices?|stops?|visits?|gallons?|hours?|PODs?|items?|drivers?|units?)\b/i;

// First sentence of a spoken reply becomes the headline when it carries a
// money figure or a count; everything else stays as body text.
function splitHeadline(text) {
  const raw = String(text || '');
  const lines = raw.split('\n');
  const firstIndex = lines.findIndex((line) => line.trim());
  const none = { headline: '', body: raw };
  if (firstIndex < 0) return none;
  const first = lines[firstIndex].trim();
  if (/^(\||[-•]\s)/.test(first)) return none;
  const [sentence = '', ...rest] = splitSentences(first);
  const headline = sentence.replace(/\*\*/g, '').trim();
  if (!headline || headline.length > 200 || !HEADLINE_FIGURE.test(headline)) return none;
  const body = [rest.join(' '), ...lines.slice(firstIndex + 1)].join('\n').trim();
  return { headline, body };
}

const CAVEAT_START = /^(?:not counted:|statuses are what['’]s saved|couldn['’]t verify:)/i;
const LIST_LINE = /^[-•]\s+/;

// Pulls "Not counted:", "Statuses are what's saved…" and "Couldn't verify:"
// (with its list) out of the reply so they render together in one box.
function extractCaveats(text) {
  const lines = String(text || '').split('\n');
  const kept = [];
  const caveats = [];
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i].trim();
    if (!line || line.startsWith('|') || LIST_LINE.test(line)) {
      kept.push(line);
      continue;
    }
    const keep = [];
    let lastCaveat = null;
    splitSentences(line).forEach((sentence) => {
      if (CAVEAT_START.test(sentence)) {
        lastCaveat = { text: sentence.replace(/\*\*/g, ''), items: [] };
        caveats.push(lastCaveat);
      } else {
        keep.push(sentence);
        lastCaveat = null;
      }
    });
    if (keep.length) kept.push(keep.join(' '));
    if (lastCaveat && lastCaveat.text.endsWith(':')) {
      let j = i + 1;
      while (j < lines.length && !lines[j].trim()) j += 1;
      while (j < lines.length && LIST_LINE.test(lines[j].trim())) {
        lastCaveat.items.push(lines[j].trim().replace(LIST_LINE, '').replace(/\*\*/g, ''));
        j += 1;
      }
      if (lastCaveat.items.length) i = j - 1;
    }
  }
  return {
    text: kept.join('\n').replace(/\n{3,}/g, '\n\n').trim(),
    caveats,
  };
}

// Headline, body and caveats are read from the reply text until the backend
// sends a structured card.
// The backend puts the short answer first and the full breakdown after this marker.
const DETAILS_MARKER = '[[details]]';

function splitDetails(text) {
  const raw = String(text || '');
  const at = raw.indexOf(DETAILS_MARKER);
  if (at < 0) return { summary: raw, details: null };
  const details = raw.slice(at + DETAILS_MARKER.length).trim();
  return { summary: raw.slice(0, at).trim(), details: details || null };
}

function answerCard(text) {
  const { text: main, caveats } = extractCaveats(text);
  const { headline, body } = splitHeadline(main);
  return { headline, body, caveats };
}

const CHIP_COLUMN = /status|pod|invoice|receiver|visit|^rc$|docs?$|documents?/i;

function classifyCell(header, value) {
  if (!CHIP_COLUMN.test(String(header || ''))) return null;
  const v = String(value || '').trim().toLowerCase();
  if (!v || v === '—' || v === '-') return null;
  if (/\bnot checked\b|\bno access\b/.test(v)) return 'muted';
  if (/\bnot attached\b|\bnone linked\b|\bnot linked\b|\bno invoice\b|\bmissing\b|^no$/.test(v)) return 'bad';
  if (/\bpicked up\b|\bnot marked delivered\b|\bchecked in\b|\bunclear\b|\bnot gps|\bin transit\b|\bpending\b/.test(v)) return 'warn';
  if (/\bdelivered\b|\battached\b|\blinked\b|\bpaid\b|\binvoiced\b|^yes$/.test(v)) return 'good';
  return null;
}

function loadCellId(header, value) {
  if (!/^load\b/i.test(String(header || '').trim())) return '';
  const id = String(value || '').trim();
  return /^[A-Za-z0-9-]{4,}$/.test(id) && /\d{4,}/.test(id) ? id : '';
}

const NUMERIC_HEADER = /rate|revenue|amount|miles|cost|total|result|pay|fuel|\$|per day|gallons/i;
const NUMERIC_VALUE = /^[-−]?\$?\s?[\d,]+(?:\.\d+)?%?$/;

function isNumericColumn(header, values = []) {
  if (/^load\b/i.test(String(header || '').trim())) return false;
  const filled = values.map((cell) => String(cell || '').trim()).filter((cell) => cell && cell !== '—');
  if (!filled.length) return NUMERIC_HEADER.test(String(header || ''));
  return filled.every((cell) => NUMERIC_VALUE.test(cell));
}

const WORKING_STEPS = [
  { until: 2500, label: 'Understanding the question' },
  { until: 6000, label: 'Reading records' },
  { until: Infinity, label: 'Checking the numbers' },
];

function workingStep(elapsedMs = 0) {
  const ms = Math.max(0, Number(elapsedMs) || 0);
  const step = WORKING_STEPS.find((row) => ms < row.until);
  return {
    label: step.label,
    seconds: ms >= 3000 ? Math.floor(ms / 1000) : null,
  };
}

function llamaRoleFor(authorization = {}) {
  const name = String(authorization?.orgRoleName || '').toLowerCase();
  if (/account|billing|bookkeep|finance|invoice/.test(name)) return 'accounting';
  if (/dispatch/.test(name)) return 'dispatcher';
  if (/owner|admin|manager|president|ceo|coo/.test(name)) return 'owner';
  const system = String(authorization?.systemRole || '');
  if (system === 'orgAdmin' || system === 'superAdmin') return 'owner';
  return 'dispatcher';
}

const QUICK_PROMPTS = {
  owner: ['How did each truck do last week?', 'Open invoices', 'Late loads'],
  dispatcher: ['Where is 2229?', 'Loads without POD', 'Late loads'],
  accounting: ['Open invoices', 'Ready to invoice', 'Loads without POD'],
};

function quickPrompts(role) {
  return (QUICK_PROMPTS[role] || QUICK_PROMPTS.owner).slice();
}

function createChatSession(initial = {}) {
  let epoch = initial.epoch || 0;
  let conversationId = initial.conversationId || newConversationId();
  let orgId = initial.orgId || null;
  let userId = initial.userId || null;
  let messages = initial.messages || [];
  let composer = initial.composer || '';
  let loading = false;
  let attachment = normalizeAttachment(initial.attachment);
  let requestId = initial.requestId || null;
  let retry = null;

  function identityMatch(sent, { matchRequestId = false } = {}) {
    return shouldAcceptChatResponse({
      sentEpoch: sent.epoch,
      activeEpoch: epoch,
      sentConversationId: sent.conversationId,
      activeConversationId: conversationId,
      sentOrgId: sent.orgId,
      activeOrgId: orgId,
      sentUserId: sent.userId,
      activeUserId: userId,
      sentRequestId: sent.requestId,
      activeRequestId: requestId,
      matchRequestId,
    });
  }

  return {
    snapshot() {
      return {
        epoch,
        conversationId,
        orgId,
        userId,
        messages: messages.slice(),
        composer,
        loading,
        attachment,
        requestId,
      };
    },
    startNewChat() {
      epoch += 1;
      conversationId = newConversationId();
      messages = [];
      composer = '';
      loading = false;
      attachment = null;
      requestId = null;
      retry = null;
      return { epoch, conversationId, orgId, userId };
    },
    setAttachment(next) {
      attachment = normalizeAttachment(next);
      return attachment;
    },
    beginSend(text) {
      const next = String(text || composer).trim();
      if (!next || loading) return null;
      if (!conversationId) conversationId = newConversationId();
      const reuse = retry
        && retry.text === next
        && JSON.stringify(retry.attachment) === JSON.stringify(attachment);
      const sentAttachment = reuse ? retry.attachment : attachment;
      const sentRequestId = reuse ? retry.requestId : newRequestId();
      const sent = {
        epoch,
        conversationId,
        orgId,
        userId,
        text: next,
        attachment: sentAttachment,
        requestId: sentRequestId,
      };
      composer = '';
      loading = true;
      requestId = sentRequestId;
      attachment = null;
      retry = null;
      const alreadyQueued = messages.some((row) => (
        row.role === 'user' && row.requestId === sentRequestId
      ));
      if (!alreadyQueued) {
        messages = [...messages, {
          role: 'user',
          text: next,
          attachment: sentAttachment,
          requestId: sentRequestId,
        }];
      }
      return sent;
    },
    applyReply(sent, row) {
      if (!identityMatch(sent, { matchRequestId: true })) return false;
      messages = [...messages, row];
      loading = false;
      retry = null;
      return true;
    },
    failSend(sent, draft) {
      if (!identityMatch(sent, { matchRequestId: true })) return false;
      composer = restoreComposer({ current: composer, draft, accepted: true });
      loading = false;
      attachment = sent.attachment || attachment;
      requestId = sent.requestId;
      retry = {
        requestId: sent.requestId,
        attachment: sent.attachment || null,
        text: sent.text,
      };
      return true;
    },
    applyHistory(sent, rows) {
      if (!identityMatch(sent)) return false;
      if (historyIsStale(messages, rows, { loading })) return false;
      messages = rows || [];
      return true;
    },
    setComposer(value) {
      composer = value;
    },
  };
}

function answerOwnsBriefing(row = {}) {
  return PNL_HEADER_KINDS.has(row.kind) && !!row.briefing;
}

const api = {
  moneyExact,
  replyText,
  missingCostLabels,
  lineFlags,
  fuelEstimateFlags,
  usesSpokenReply,
  spokenWindow,
  visibleAnswer,
  documentSlotLabel,
  documentSlotValue,
  evidencedDocumentSlots,
  shouldShowDocumentTable,
  immediatePanel,
  coverageLabel,
  freshnessLabel,
  milesCoverageRows,
  unitMilesCoverage,
  fleetMilesCoverageNotice,
  shouldAcceptChatResponse,
  historyIsStale,
  restoreComposer,
  newConversationId,
  newRequestId,
  isLegacyLlamaEntry,
  canonicalLlamaPath,
  threadStorageKey,
  normalizeAttachment,
  attachmentLabel,
  conversationContextLine,
  mpgComparisonLine,
  turnSubjectLine,
  openLoadNumber,
  windowLoadLabel,
  headerContextLine,
  headerMpgLine,
  answerOwnsBriefing,
  immediateQualifications,
  replyBlocks,
  splitSentences,
  splitHeadline,
  extractCaveats,
  answerCard,
  splitDetails,
  classifyCell,
  loadCellId,
  isNumericColumn,
  workingStep,
  llamaRoleFor,
  quickPrompts,
  createChatSession,
};

module.exports = api;
module.exports.default = api;
